import React, { useState } from "react";
import Swal from "sweetalert2";
import { skipToken } from "@reduxjs/toolkit/query";
import { useProjectContext } from "../layout/ProjectContext";
import { useGetProjectsQuery } from "../features/dashboard/apiSlice";
import {
  useListReportsQuery,
  useGenerateReportMutation,
  useLazyDownloadStoredReportQuery,
  ReportRecord,
} from "../features/reports/apiSlice";
import EmptyHint from "../components/EmptyHint";
import dayjs from "dayjs";

const Reports: React.FC = () => {
  const { projectId } = useProjectContext();
  const { data: projects } = useGetProjectsQuery();
  // Initialize with first and last day of current month
  const now = dayjs();
  const firstDay = now.startOf("month");
  const lastDay = now.endOf("month");

  const [from, setFrom] = useState<string>(firstDay.format("YYYY-MM-DD"));
  const [to, setTo] = useState<string>(lastDay.format("YYYY-MM-DD"));
  const [submitting, setSubmitting] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const currentProject = projects?.find((p) => p.id === projectId) ?? null;

  const { data: reports } = useListReportsQuery(projectId ?? skipToken, {
    // Refresh automatically while any report is still generating.
    pollingInterval: 4000,
    refetchOnMountOrArgChange: true,
  });

  const [generateReport] = useGenerateReportMutation();
  const [triggerDownload] = useLazyDownloadStoredReportQuery();

  if (projectId == null) {
    return (
      <EmptyHint
        icon="bi-folder2-open"
        title="Select a project"
        subtitle="Choose a project from the top bar to generate reports."
      />
    );
  }

  const startGeneration = async () => {
    setSubmitting(true);
    try {
      await generateReport({ projectId, from, to }).unwrap();
      await Swal.fire({
        icon: "success",
        title: "Report queued",
        text: `The report for ${from} → ${to} is now being generated. You can leave this page — it will keep building in the background.`,
        timer: 2600,
        showConfirmButton: false,
      });
    } catch (err) {
      console.log("Error starting report generation:", err);
      await Swal.fire({
        icon: "error",
        title: "Could not start generation",
        text: "Something went wrong while queuing the report.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerate = async () => {
    if (from > to) {
      await Swal.fire({
        icon: "warning",
        title: "Invalid date range",
        text: "The 'From' date cannot be later than the 'To' date.",
      });
      return;
    }

    // Warn if an identical report (same project + range) already exists.
    const duplicate = reports?.find(
      (r) =>
        dayjs(r.from_date).format("YYYY-MM-DD") === from &&
        dayjs(r.to_date).format("YYYY-MM-DD") === to &&
        (r.status === "ready" || r.status === "generating"),
    );

    if (duplicate) {
      const result = await Swal.fire({
        icon: "warning",
        title: "Report already exists",
        html: `A report for <b>${from} → ${to}</b> is already ${
          duplicate.status === "ready" ? "available" : "being generated"
        }.<br/>Do you still want to generate it again?`,
        showCancelButton: true,
        confirmButtonText: "Generate anyway",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#d97706",
      });
      if (!result.isConfirmed) return;
    }

    await startGeneration();
  };

  const handleDownload = async (report: ReportRecord) => {
    setDownloadingId(report.id);
    try {
      const blob = await triggerDownload(report.id).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // The API returns review_report.docx extracted from the zip and renamed.
      const shiftDate = (raw: string) => {
        const d = new Date(raw);
        d.setTime(d.getTime() + 12 * 60 * 60 * 1000);
        return [
          d.getUTCFullYear(),
          String(d.getUTCMonth() + 1).padStart(2, "0"),
          String(d.getUTCDate()).padStart(2, "0"),
        ].join("");
      };
      a.download = `${report.project_code}_${shiftDate(report.from_date)}_${shiftDate(report.to_date)}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.log("Error downloading report:", err);
      await Swal.fire({
        icon: "error",
        title: "Download failed",
        text: "Could not download this report.",
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const formatDate = (value: string | null) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return dayjs(value).format("YYYY-MM-DD");
    return d.toLocaleString();
  };

  const formatDay = (value: string) => {
    const d = new Date(value);
    d.setTime(d.getTime() + 12 * 60 * 60 * 1000);
    return [
      d.getUTCFullYear(),
      String(d.getUTCMonth() + 1).padStart(2, "0"),
      String(d.getUTCDate()).padStart(2, "0"),
    ].join("-");
  };

  const renderStatus = (report: ReportRecord) => {
    if (report.status === "ready") {
      return (
        <button
          className="btn btn-primary btn-sm"
          onClick={() => handleDownload(report)}
          disabled={downloadingId === report.id}
        >
          <i
            className={`bi ${
              downloadingId === report.id ? "bi-hourglass-split" : "bi-download"
            }`}
          />{" "}
          {downloadingId === report.id ? "Downloading…" : "Download"}
        </button>
      );
    }
    if (report.status === "generating") {
      return (
        <span className="report-badge generating">
          <span className="report-spinner" /> Generating…
        </span>
      );
    }
    return (
      <span
        className="report-badge failed"
        title={report.error_message ?? undefined}
      >
        <i className="bi bi-exclamation-triangle" /> Failed
      </span>
    );
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Reports</h1>
          <div className="sub">
            {currentProject?.name ?? `Project #${projectId}`} · monitoring
            bundle
          </div>
        </div>
      </div>

      <div className="card" style={{ maxWidth: 560 }}>
        <div className="card-head">
          <h2>Generate Report</h2>
        </div>
        <div className="card-body">
          <div className="row gap-3 mb-3">
            <div className="field" style={{ maxWidth: 240 }}>
              <label>From</label>
              <input
                className="input"
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </div>
            <div className="field" style={{ maxWidth: 240 }}>
              <label>To</label>
              <input
                className="input"
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>
          </div>
          <button
            className="btn btn-primary"
            onClick={handleGenerate}
            disabled={submitting}
          >
            <i
              className={`bi ${
                submitting ? "bi-hourglass-split" : "bi-play-fill"
              }`}
            />{" "}
            Generate Report
          </button>
          <div className="report-hint">
            Generation runs on the server. You can safely close this page — the
            report keeps building and will appear in the table below once ready.
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 20 }}>
        <div className="card-head">
          <h2>Generated Reports</h2>
        </div>
        <div className="card-body">
          {!reports || reports.length === 0 ? (
            <div className="report-empty">
              No reports yet. Generate one above to get started.
            </div>
          ) : (
            <div className="report-table-wrap">
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Build Date</th>
                    <th>Project Name</th>
                    <th>Project Code</th>
                    <th>From</th>
                    <th>To</th>
                    <th style={{ textAlign: "right" }}>Download</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report) => (
                    <tr key={report.id}>
                      <td>{formatDate(report.created_at)}</td>
                      <td>{report.project_name}</td>
                      <td>{report.project_code}</td>
                      <td>{formatDay(report.from_date)}</td>
                      <td>{formatDay(report.to_date)}</td>
                      <td style={{ textAlign: "right" }}>
                        {renderStatus(report)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .report-hint {
          margin-top: 12px;
          font-size: 13px;
          color: #64748b;
          line-height: 1.5;
        }
        .report-empty {
          color: #64748b;
          font-size: 14px;
          padding: 12px 4px;
        }
        .report-table-wrap {
          overflow-x: auto;
        }
        .report-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
        }
        .report-table th,
        .report-table td {
          padding: 12px 14px;
          text-align: left;
          border-bottom: 1px solid #e2e8f0;
          white-space: nowrap;
        }
        .report-table th {
          font-weight: 600;
          color: #475569;
          background: #f8fafc;
        }
        .report-table tbody tr:hover {
          background: #f8fafc;
        }
        .btn-sm {
          padding: 6px 12px;
          font-size: 13px;
        }
        .report-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          font-weight: 500;
          padding: 4px 10px;
          border-radius: 999px;
        }
        .report-badge.generating {
          background: #eff6ff;
          color: #2563eb;
        }
        .report-badge.failed {
          background: #fef2f2;
          color: #dc2626;
          cursor: help;
        }
        .report-spinner {
          width: 13px;
          height: 13px;
          border-radius: 999px;
          border: 2px solid rgba(37, 99, 235, 0.35);
          border-top-color: #2563eb;
          animation: reportSpin 0.8s linear infinite;
        }
        @keyframes reportSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default Reports;
